import { getAuth } from "firebase/auth";
import { getApiUrl } from "./config";

const getBase = () => getApiUrl();

export async function getDraftTenants() {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${getBase()}/onboarding/draft-tenants`, {
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to load draft tenants (${res.status})`
    );
  }
  return res.json();
}

/** Creates $55 application fee Stripe Checkout (path param). Redirects to Stripe on success. */
export async function createApplicationFeeSession(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/create-application-fee-session`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to create application fee session (${res.status})`
    );
  }
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  return data;
}

/** Emails the applicant pay + application links; screening email follows after payment (webhook). */
export async function sendLeaseUpApplicationPackageEmail(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/send-application-package-email`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error ||
        `Failed to send application package email (${res.status})`
    );
  }
  return res.json();
}

/** Creates $55 application fee Stripe Checkout (body: { tenantId }). Same as createApplicationFeeSession. */
export async function createAppFeeSession(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${getBase()}/onboarding/create-app-fee-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify({ tenantId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to create application fee session (${res.status})`
    );
  }
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  return data;
}

export async function triggerScreening(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/trigger-screening`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to trigger screening (${res.status})`);
  }
  return res.json();
}

/**
 * POST /api/onboarding/tenants/:tenantId/documents
 * Multipart: file + type ('photo_id'|'paystub'|'w2')
 */
export async function addApplicantDocument(tenantId, { type, file }) {
  if (!type || !file) {
    throw new Error("Document type and file are required");
  }
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const form = new FormData();
  form.append("type", type);
  form.append("file", file, file.name || "document");
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(tenantId)}/documents`,
    {
      method: "POST",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: form,
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to add document (${res.status})`);
  }
  return res.json();
}

/**
 * DELETE /api/onboarding/tenants/:tenantId/documents
 * Body: { url }
 */
export async function removeApplicantDocument(tenantId, { url }) {
  if (!url) {
    throw new Error("Document url is required");
  }
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(tenantId)}/documents`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ url }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to remove document (${res.status})`);
  }
  return res.json();
}

export async function generateAndSendLease(tenantId, leaseFile) {
  if (!leaseFile) {
    throw new Error(
      "Lease PDF is required. Upload your lease document to send for signature."
    );
  }
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const form = new FormData();
  form.append("file", leaseFile, leaseFile.name || "lease.pdf");
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/generate-and-send-lease`,
    {
      method: "POST",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: form,
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to generate and send lease (${res.status})`
    );
  }
  return res.json();
}

/**
 * DELETE /api/onboarding/tenants/:tenantId
 * Remove an applicant from the Lease-Ups pipeline.
 */
export async function deleteLeaseUp(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(tenantId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to delete lease-up (${res.status})`
    );
  }
  return res.json();
}

/**
 * POST /api/onboarding/tenants/:tenantId/mark-signed
 * Manually mark applicant as lease signed.
 */
export async function markApplicantLeaseSigned(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/mark-signed`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to mark lease signed (${res.status})`
    );
  }
  return res.json();
}

/** Create first month prorated rent + security deposit charges for the tenant (after sending lease). */
export async function initializeLedger(tenantId) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(
    `${getBase()}/onboarding/tenants/${encodeURIComponent(
      tenantId
    )}/initialize-ledger`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to initialize ledger (${res.status})`);
  }
  return res.json();
}
